import { useCallback } from "react";
import { showErrorAlert, showSuccessAlert } from "../../../../actions";
import { useDispatch } from "../../../../contexts/snackbar-context";

// Older in-app browsers lack the async clipboard API, or refuse it without a secure context.
const copyWithSelection = (text: string) => {
  const focused = document.activeElement as HTMLElement | null;
  const field = document.createElement("textarea");
  field.value = text;
  // Read-only so focusing it does not flash the keyboard on iOS.
  field.setAttribute("readonly", "");
  // 16px stops iOS zooming in on focus; fixed at the top so the page does not jump.
  Object.assign(field.style, { position: "fixed", top: "0", left: "0", opacity: "0", fontSize: "16px" });
  document.body.appendChild(field);
  field.focus();
  // iOS ignores select() alone.
  field.setSelectionRange(0, text.length);
  const copied = document.execCommand("copy");
  document.body.removeChild(field);
  focused?.focus?.();
  if (!copied) throw new Error("Copy was refused");
};

/** Copies `text`, then says so in a toast naming what was copied (e.g. "VIN copied"). */
export const useCopyToClipboard = () => {
  const dispatch = useDispatch();
  return useCallback(
    async (text: string, label: string) => {
      try {
        if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(text);
        // Still inside the tap, which the fallback needs on Safari.
        else copyWithSelection(text);
        showSuccessAlert(dispatch, `${label} copied`);
      } catch {
        // After a refused async copy the tap is over, so Safari may refuse this too; then say so.
        try {
          copyWithSelection(text);
          showSuccessAlert(dispatch, `${label} copied`);
        } catch {
          showErrorAlert(dispatch, `Couldn't copy the ${label}`);
        }
      }
    },
    [dispatch]
  );
};
